/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  DownloadSimple, X, FileText, Image as ImageIcon, Warning,
  CaretLeft, CaretRight, MagnifyingGlassPlus, MagnifyingGlassMinus,
  ArrowsClockwise, Folder, FolderOpen, FilePdf, FileImage, MagnifyingGlass,
  Columns, SquaresFour, SidebarSimple, LinkSimple, ArrowsOutLineHorizontal, ArrowsLeftRight,
} from "@phosphor-icons/react";

// Load pdf.js worker from a CDN pinned to the exact installed version so
// the worker API stays in sync with react-pdf's bundled pdfjs API.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const typeOf = (doc) => {
  const filename = doc?.original_filename || doc?.filename || "";
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  const mime = (doc?.mime_type || doc?.content_type || "").toLowerCase();
  const isPdf = mime === "application/pdf" || ext === "pdf";
  const isImage = mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg", "heic", "heif"].includes(ext);
  const isText = mime.startsWith("text/") || ["txt", "csv", "json", "md", "log", "xml", "html", "js", "py"].includes(ext);
  const isOfficeBinary = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext);
  return { filename, ext, mime, isPdf, isImage, isText, isOfficeBinary };
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

/**
 * In-place document viewer for the case Documents tab. Panes:
 *   • Left  — a navigable list of the case's documents (grouped by folder)
 *   • Right — an inline preview of the selected file; a "Compare" toggle
 *             splits this into two preview panes so a deed can be read
 *             against the register side-by-side.
 * PDFs can show a page-thumbnail rail for quick navigation of long files.
 */
export default function DocumentViewerDialog({
  doc, documents = [], folders = [], open, onClose, initialPage = null, highlightTerms = "",
}) {
  const [activeDoc, setActiveDoc] = useState(doc);
  const [compareDoc, setCompareDoc] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [scrollLock, setScrollLock] = useState(false);
  const scrollSyncRef = useRef({ panes: {}, syncing: false, enabled: false });
  const [sidebarQuery, setSidebarQuery] = useState("");
  const [expandedFolders, setExpandedFolders] = useState(() => new Set());

  useEffect(() => { scrollSyncRef.current.enabled = scrollLock && compareMode; }, [scrollLock, compareMode]);

  useEffect(() => {
    if (open) {
      setActiveDoc(doc);
      setCompareDoc(null);
      setCompareMode(false);
      setSidebarCollapsed(false);
      setScrollLock(false);
      scrollSyncRef.current.panes = {};
      setSidebarQuery("");
      if (doc?.folder_id) setExpandedFolders((prev) => new Set(prev).add(doc.folder_id));
    }
  }, [open, doc]);

  const { filename, ext, isImage } = typeOf(activeDoc);

  const toggleCompare = () => {
    setCompareMode((on) => {
      const next = !on;
      if (next && !compareDoc) {
        const other = (documents || []).find((d) => d.id !== activeDoc?.id);
        setCompareDoc(other || null);
      }
      return next;
    });
  };

  // Build the sidebar tree: folders (with their docs) followed by root docs.
  const tree = useMemo(() => {
    const q = sidebarQuery.trim().toLowerCase();
    const match = (d) => !q || (d.original_filename || d.filename || "").toLowerCase().includes(q);
    const all = (documents || []).filter(match);
    const byFolder = new Map();
    const root = [];
    for (const d of all) {
      if (d.folder_id) {
        if (!byFolder.has(d.folder_id)) byFolder.set(d.folder_id, []);
        byFolder.get(d.folder_id).push(d);
      } else {
        root.push(d);
      }
    }
    const folderNodes = (folders || [])
      .map((f) => ({ folder: f, docs: byFolder.get(f.id) || [] }))
      .filter((n) => n.docs.length > 0 || !q);
    const knownIds = new Set((folders || []).map((f) => f.id));
    for (const [fid, docs] of byFolder.entries()) {
      if (!knownIds.has(fid)) root.push(...docs);
    }
    return { folderNodes, root };
  }, [documents, folders, sidebarQuery]);

  const handleDownload = async (d) => {
    const t = typeOf(d);
    try {
      const resp = await API.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const inferredMime = d.mime_type || t.mime || (t.isPdf
        ? "application/pdf"
        : t.ext === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : t.ext === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : t.ext === "pptx" ? "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        : "application/octet-stream");
      const blob = new Blob([resp.data], { type: inferredMime });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        const a = document.createElement("a");
        a.href = url; a.download = t.filename;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  if (!doc) return null;

  const FileRow = ({ d }) => {
    const t = typeOf(d);
    const Icon = t.isImage ? FileImage : t.isPdf ? FilePdf : FileText;
    const isActive = activeDoc?.id === d.id;
    return (
      <button
        type="button"
        onClick={() => setActiveDoc(d)}
        data-testid={`viewer-file-${d.id}`}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
          isActive ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-slate-100"
        }`}
        title={d.original_filename || d.filename}
      >
        <Icon weight="fill" className={`w-4 h-4 shrink-0 ${t.isPdf ? "text-rose-500" : t.isImage ? "text-emerald-500" : "text-blue-500"}`} />
        <span className={`text-xs truncate ${isActive ? "text-blue-800 font-medium" : "text-slate-700"}`}>
          {d.original_filename || d.filename}
        </span>
        {compareMode && compareDoc?.id === d.id && (
          <span className="ml-auto text-[9px] font-semibold text-indigo-600 shrink-0">B</span>
        )}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={`${compareMode ? "sm:max-w-[97vw] w-[97vw]" : "sm:max-w-6xl"} h-[92vh] max-h-[92vh] p-0 flex flex-col overflow-hidden`}
        data-testid="document-viewer-dialog"
      >
        <DialogHeader className="px-4 py-3 border-b border-slate-100 flex flex-row items-center justify-between gap-2 space-y-0 shrink-0">
          <DialogTitle className="text-sm font-medium truncate flex items-center gap-2 min-w-0" style={{ fontFamily: "Outfit, sans-serif" }}>
            {isImage ? <ImageIcon weight="fill" className="w-4 h-4 text-blue-500 shrink-0" /> : <FileText weight="fill" className="w-4 h-4 text-blue-500 shrink-0" />}
            <span className="truncate">{filename}</span>
          </DialogTitle>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              onClick={() => setSidebarCollapsed((c) => !c)}
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1"
              data-testid="doc-viewer-sidebar-toggle"
              title={sidebarCollapsed ? "Show file list" : "Hide file list"}
            >
              <SidebarSimple weight="bold" className="w-3.5 h-3.5" />
              {sidebarCollapsed ? "Files" : "Hide list"}
            </Button>
            <Button
              onClick={toggleCompare}
              variant={compareMode ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs gap-1"
              data-testid="doc-viewer-compare-toggle"
              title="Open a second document side-by-side"
            >
              <Columns weight="bold" className="w-3.5 h-3.5" /> Compare
            </Button>
            {compareMode && (
              <Button
                onClick={() => setScrollLock((s) => !s)}
                variant={scrollLock ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs gap-1"
                data-testid="doc-viewer-scrolllock-toggle"
                title="Scroll both documents together"
              >
                <LinkSimple weight="bold" className="w-3.5 h-3.5" /> {scrollLock ? "Synced" : "Sync scroll"}
              </Button>
            )}
            {compareMode && compareDoc && (
              <Button
                onClick={() => { const a = activeDoc; setActiveDoc(compareDoc); setCompareDoc(a); }}
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                data-testid="doc-viewer-swap-btn"
                title="Swap the two documents"
              >
                <ArrowsLeftRight weight="bold" className="w-3.5 h-3.5" /> Swap
              </Button>
            )}
            <Button onClick={() => handleDownload(activeDoc)} variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid="doc-viewer-download-btn">
              <DownloadSimple weight="bold" className="w-3.5 h-3.5" /> Download
            </Button>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100"
              data-testid="doc-viewer-close-btn"
            >
              <X weight="bold" className="w-4 h-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* ---- Left pane: file list (collapsible) ---------------------- */}
          {!sidebarCollapsed && (
          <aside className="w-72 shrink-0 border-r border-slate-200 flex flex-col bg-slate-50/60 min-h-0" data-testid="doc-viewer-sidebar">
            <div className="px-3 py-2.5 border-b border-slate-200">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Documents ({documents.length})</p>
              <div className="relative">
                <MagnifyingGlass weight="bold" className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  value={sidebarQuery}
                  onChange={(e) => setSidebarQuery(e.target.value)}
                  placeholder="Filter files…"
                  data-testid="doc-viewer-filter"
                  className="w-full h-7 pl-7 pr-2 text-xs rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
              {tree.folderNodes.map(({ folder, docs }) => {
                const isExp = expandedFolders.has(folder.id);
                return (
                  <div key={folder.id}>
                    <button
                      type="button"
                      onClick={() => setExpandedFolders((prev) => {
                        const next = new Set(prev);
                        if (next.has(folder.id)) next.delete(folder.id); else next.add(folder.id);
                        return next;
                      })}
                      data-testid={`viewer-folder-${folder.id}`}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
                    >
                      {isExp ? <FolderOpen weight="fill" className="w-4 h-4 text-amber-500 shrink-0" /> : <Folder weight="fill" className="w-4 h-4 text-amber-500 shrink-0" />}
                      <span className="text-xs font-medium text-slate-700 truncate">{folder.name}</span>
                      <span className="text-[10px] text-slate-400 ml-auto">{docs.length}</span>
                    </button>
                    {isExp && (
                      <div className="pl-3 space-y-0.5">
                        {docs.map((d) => <FileRow key={d.id} d={d} />)}
                      </div>
                    )}
                  </div>
                );
              })}
              {tree.root.map((d) => <FileRow key={d.id} d={d} />)}
              {documents.length === 0 && (
                <p className="text-xs text-slate-400 px-2 py-4 text-center">No documents</p>
              )}
            </div>
            {/* Metadata footer for the active file */}
            {activeDoc && (
              <div className="border-t border-slate-200 px-3 py-2.5 bg-white text-[11px] space-y-1" data-testid="doc-viewer-meta">
                <p className="font-medium text-slate-700 truncate">{filename}</p>
                <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-slate-500">
                  <span className="text-slate-400">Created</span><span className="truncate">{fmtDate(activeDoc.created_at)}</span>
                  {activeDoc.uploaded_by_name && (<><span className="text-slate-400">By</span><span className="truncate">{activeDoc.uploaded_by_name}</span></>)}
                  <span className="text-slate-400">Type</span><span className="uppercase">{ext || "—"}</span>
                </div>
              </div>
            )}
          </aside>
          )}

          {/* ---- Right: one or two preview panes ------------------------- */}
          <div className="flex-1 flex min-w-0 min-h-0">
            <PreviewPane
              doc={activeDoc}
              highlightTerms={activeDoc?.id === doc?.id ? highlightTerms : ""}
              initialPage={activeDoc?.id === doc?.id ? initialPage : null}
              paneKey="a"
              scrollSyncRef={scrollSyncRef}
              testId="doc-viewer-pane-a"
            />
            {compareMode && (
              <div className="flex-1 flex flex-col border-l border-slate-200 min-w-0 min-h-0" data-testid="doc-viewer-pane-b-wrap">
                <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-200 bg-white shrink-0">
                  <span className="text-[10px] font-semibold text-indigo-600 shrink-0">B</span>
                  <select
                    value={compareDoc?.id || ""}
                    onChange={(e) => setCompareDoc((documents || []).find((d) => d.id === e.target.value) || null)}
                    data-testid="doc-viewer-compare-select"
                    className="flex-1 min-w-0 h-7 text-xs rounded-md border border-slate-200 bg-white px-2 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  >
                    <option value="">Select a document…</option>
                    {(documents || []).map((d) => (
                      <option key={d.id} value={d.id}>{d.original_filename || d.filename}</option>
                    ))}
                  </select>
                  {compareDoc && (
                    <button
                      type="button"
                      onClick={() => handleDownload(compareDoc)}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 shrink-0"
                      title="Download"
                      data-testid="doc-viewer-compare-download"
                    >
                      <DownloadSimple weight="bold" className="w-3.5 h-3.5 text-slate-600" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setCompareMode(false)}
                    className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 shrink-0"
                    title="Close compare"
                    data-testid="doc-viewer-compare-close"
                  >
                    <X weight="bold" className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                </div>
                <div className="flex-1 min-h-0 flex flex-col">
                  {compareDoc
                    ? <PreviewPane doc={compareDoc} paneKey="b" scrollSyncRef={scrollSyncRef} testId="doc-viewer-pane-b" />
                    : <p className="text-center text-xs text-slate-400 py-12">Pick a document to compare.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A self-contained preview pane: streams the file into a blob and renders
 * PDF / image / text inline. Used once on its own, or twice in compare mode.
 */
function PreviewPane({ doc, highlightTerms = "", initialPage = null, testId, paneKey = "a", scrollSyncRef = null }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [textPreview, setTextPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { filename, isPdf, isImage, isText, isOfficeBinary, mime } = typeOf(doc);

  useEffect(() => {
    if (!doc) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    setTextPreview(null);
    (async () => {
      try {
        const resp = await API.get(`/documents/${doc.id}/download`, { responseType: "blob" });
        if (cancelled) return;
        if (isText) {
          const txt = await resp.data.text();
          setTextPreview(txt.length > 100000 ? `${txt.slice(0, 100000)}\n\n…(truncated — download for full file)` : txt);
        } else {
          const inferredMime = mime || (isPdf ? "application/pdf" : "application/octet-stream");
          setBlobUrl(URL.createObjectURL(new Blob([resp.data], { type: inferredMime })));
        }
      } catch (err) {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load document");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [doc, isText, isPdf, mime]);

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-slate-100" data-testid={testId}>
      {loading && <p className="text-center text-xs text-slate-400 py-12">Loading preview…</p>}
      {error && (
        <div className="text-center py-12 px-4">
          <Warning weight="fill" className="w-6 h-6 text-amber-500 mx-auto mb-2" />
          <p className="text-xs text-rose-600">{error}</p>
        </div>
      )}
      {!loading && !error && isPdf && blobUrl && (
        <PdfViewer url={blobUrl} highlightTerms={highlightTerms} initialPage={initialPage} paneKey={paneKey} scrollSyncRef={scrollSyncRef} />
      )}
      {!loading && !error && isImage && blobUrl && (
        <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-4">
          <img src={blobUrl} alt={filename} className="max-w-full max-h-full object-contain shadow" data-testid="doc-viewer-image" />
        </div>
      )}
      {!loading && !error && isText && textPreview != null && (
        <div className="flex-1 min-h-0 overflow-auto">
          <pre className="text-[11px] font-mono leading-relaxed text-slate-700 p-4 whitespace-pre-wrap break-words" data-testid="doc-viewer-text">{textPreview}</pre>
        </div>
      )}
      {!loading && !error && !isPdf && !isImage && !isText && (
        <div className="flex-1 overflow-auto text-center py-12 px-4" data-testid="doc-viewer-unsupported">
          <FileText weight="fill" className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-700 font-medium">
            {isOfficeBinary
              ? "Office documents (Word / Excel / PowerPoint) can't be previewed inline yet."
              : "Preview not available for this file type."}
          </p>
          <p className="text-xs text-slate-400 mt-1">Use the Download button to open it locally.</p>
        </div>
      )}
    </div>
  );
}

/**
 * Embedded PDF viewer with a toolbar (prev/next/zoom/rotate + thumbnail
 * toggle) and an optional left rail of page thumbnails for quick jumping.
 */
function PdfViewer({ url, highlightTerms, initialPage, paneKey = "a", scrollSyncRef = null }) {
  const file = useMemo(() => ({ url }), [url]);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(initialPage && initialPage > 0 ? initialPage : 1);
  const [scale, setScale] = useState(1.2);
  const [rotation, setRotation] = useState(0);
  const [showThumbs, setShowThumbs] = useState(false);
  const [docError, setDocError] = useState(null);
  const scrollAreaRef = useRef(null);
  const pageDimsRef = useRef(null);
  const autoFitRef = useRef(false);

  // Fresh document → allow it to auto-fit to the pane width on first render.
  useEffect(() => { autoFitRef.current = false; }, [url]);

  // Register this pane's scroll element for compare sync-scroll, and keep
  // a local ref for fit-to-width measurements.
  const setScrollEl = (el) => {
    scrollAreaRef.current = el;
    if (scrollSyncRef && el) scrollSyncRef.current.panes[paneKey] = el;
  };

  const handleSyncScroll = (e) => {
    const sync = scrollSyncRef?.current;
    if (!sync?.enabled || sync.syncing) return;
    const other = sync.panes[paneKey === "a" ? "b" : "a"];
    const src = e.currentTarget;
    if (!other || other === src) return;
    sync.syncing = true;
    const yf = src.scrollTop / Math.max(1, src.scrollHeight - src.clientHeight);
    const xf = src.scrollLeft / Math.max(1, src.scrollWidth - src.clientWidth);
    other.scrollTop = yf * Math.max(0, other.scrollHeight - other.clientHeight);
    other.scrollLeft = xf * Math.max(0, other.scrollWidth - other.clientWidth);
    window.requestAnimationFrame(() => { sync.syncing = false; });
  };

  const computeFitScale = () => {
    const el = scrollAreaRef.current;
    const dims = pageDimsRef.current;
    if (!el || !dims) return null;
    const avail = el.clientWidth - 24;
    const base = rotation % 180 === 0 ? dims.w : dims.h;
    if (base <= 0) return null;
    return +Math.max(0.25, Math.min(4, avail / base)).toFixed(3);
  };

  const fitWidth = () => {
    const s = computeFitScale();
    if (s) setScale(s);
  };

  const highlightRegex = useMemo(() => {
    const raw = (highlightTerms || "").trim();
    if (!raw) return null;
    const tokens = raw.split(/\s+/).filter((t) => t.length > 1).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    if (!tokens.length) return null;
    return new RegExp(`(${tokens.join("|")})`, "gi");
  }, [highlightTerms]);

  const customTextRenderer = useMemo(() => {
    if (!highlightRegex) return undefined;
    return ({ str }) => {
      if (!str) return "";
      const escaped = str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      return escaped.replace(highlightRegex, '<mark class="bg-yellow-300/80 text-slate-900 rounded px-0.5">$1</mark>');
    };
  }, [highlightRegex]);

  const canPrev = pageNumber > 1;
  const canNext = pageNumber < numPages;

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="doc-viewer-pdf">
      <div className="sticky top-0 z-10 flex items-center justify-center gap-1.5 px-3 py-2 bg-white/95 border-b border-slate-200 backdrop-blur-sm shrink-0" data-testid="pdf-toolbar">
        <button
          type="button"
          onClick={() => setShowThumbs((s) => !s)}
          disabled={numPages <= 1}
          className={`w-7 h-7 flex items-center justify-center rounded disabled:opacity-30 ${showThumbs ? "bg-blue-100 text-blue-700" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="pdf-thumbs-toggle"
          aria-label="Toggle page thumbnails"
          title="Page thumbnails"
        >
          <SquaresFour weight="bold" className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-slate-200 mx-0.5" />
        <button type="button" onClick={() => canPrev && setPageNumber((n) => Math.max(1, n - 1))} disabled={!canPrev}
          className="w-7 h-7 flex items-center justify-center rounded text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="pdf-prev-btn" aria-label="Previous page">
          <CaretLeft weight="bold" className="w-3.5 h-3.5" />
        </button>
        <span className="text-[11px] text-slate-700 tabular-nums px-2" data-testid="pdf-pageinfo">{numPages ? `${pageNumber} / ${numPages}` : "—"}</span>
        <button type="button" onClick={() => canNext && setPageNumber((n) => Math.min(numPages, n + 1))} disabled={!canNext}
          className="w-7 h-7 flex items-center justify-center rounded text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="pdf-next-btn" aria-label="Next page">
          <CaretRight weight="bold" className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-slate-200 mx-1" />
        <button type="button" onClick={() => setScale((s) => Math.max(0.5, +(s - 0.2).toFixed(2)))}
          className="w-7 h-7 flex items-center justify-center rounded text-slate-600 hover:bg-slate-100" data-testid="pdf-zoom-out-btn" aria-label="Zoom out">
          <MagnifyingGlassMinus weight="bold" className="w-3.5 h-3.5" />
        </button>
        <span className="text-[11px] text-slate-700 tabular-nums px-1 w-12 text-center" data-testid="pdf-zoom-level">{Math.round(scale * 100)}%</span>
        <button type="button" onClick={() => setScale((s) => Math.min(3, +(s + 0.2).toFixed(2)))}
          className="w-7 h-7 flex items-center justify-center rounded text-slate-600 hover:bg-slate-100" data-testid="pdf-zoom-in-btn" aria-label="Zoom in">
          <MagnifyingGlassPlus weight="bold" className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={fitWidth}
          className="w-7 h-7 flex items-center justify-center rounded text-slate-600 hover:bg-slate-100" data-testid="pdf-fit-width-btn" aria-label="Fit to width" title="Fit to width">
          <ArrowsOutLineHorizontal weight="bold" className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-slate-200 mx-1" />
        <button type="button" onClick={() => setRotation((r) => (r + 90) % 360)}
          className="w-7 h-7 flex items-center justify-center rounded text-slate-600 hover:bg-slate-100" data-testid="pdf-rotate-btn" aria-label="Rotate">
          <ArrowsClockwise weight="bold" className="w-3.5 h-3.5" />
        </button>
      </div>

      <Document
        file={file}
        onLoadSuccess={({ numPages: n }) => { setNumPages(n); setDocError(null); }}
        onLoadError={(err) => setDocError(err?.message || "Failed to render PDF")}
        loading={<p className="text-xs text-slate-400 mt-12 text-center">Rendering PDF…</p>}
        className="flex-1 min-h-0 flex"
      >
        {docError && (
          <div className="text-center py-12 px-4 w-full">
            <Warning weight="fill" className="w-6 h-6 text-amber-500 mx-auto mb-2" />
            <p className="text-xs text-rose-600">{docError}</p>
          </div>
        )}
        {showThumbs && numPages > 1 && (
          <div className="w-28 shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50 p-2 space-y-2" data-testid="pdf-thumb-rail">
            {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPageNumber(n)}
                data-testid={`pdf-thumb-${n}`}
                className={`block w-full rounded border transition-colors ${n === pageNumber ? "border-blue-500 ring-1 ring-blue-300" : "border-slate-200 hover:border-slate-400"}`}
                title={`Page ${n}`}
              >
                <Page pageNumber={n} width={88} renderTextLayer={false} renderAnnotationLayer={false} loading="" />
                <span className="block text-[9px] text-center text-slate-500 py-0.5">{n}</span>
              </button>
            ))}
          </div>
        )}
        <div ref={setScrollEl} onScroll={handleSyncScroll} className="flex-1 overflow-auto flex items-start justify-center py-4 px-2">
          <Page
            pageNumber={pageNumber}
            scale={scale}
            rotate={rotation}
            renderTextLayer
            renderAnnotationLayer
            customTextRenderer={customTextRenderer}
            className="shadow-lg"
            onLoadSuccess={(p) => {
              pageDimsRef.current = { w: p.originalWidth, h: p.originalHeight };
              if (!autoFitRef.current) {
                autoFitRef.current = true;
                const s = computeFitScale();
                if (s) setScale(s);
              }
            }}
            loading={<p className="text-xs text-slate-400 mt-4">Loading page {pageNumber}…</p>}
          />
        </div>
      </Document>
    </div>
  );
}
